<script setup lang="ts">
import {computed, reactive, ref, watch} from "vue";
import User from "@/Models/User";
import {AutoComplete, Avatar, Button, Dialog, Icon, Label, Menu} from "@/Components/Library";
import {getContacts as getContactsFromApi} from "@/Services/contacts";
import {useToast} from "primevue/usetoast";
import {watchDebounced} from "@vueuse/core";
import type {AutoCompleteCompleteEvent} from "primevue/autocomplete";

const $user = new User();
const toast = useToast();

const modelValue = defineModel<boolean>({required: true});
const props = defineProps<{
    conversation: any;
}>();

const state = reactive({
    show: {
        participant: false,
        participantMenu: false,
    },
    loading: {
        contacts: true,
    },
});

const searchQuery = ref("");

const handleSearch = (event: AutoCompleteCompleteEvent) => {
    searchQuery.value = event.query;
};

const emit = defineEmits(["removeParticipant", "addParticipants"]);
const contacts = ref<Array<any>>([]);
const totalContacts = ref(0);
const availableContacts = computed(() => {
    return contacts.value.filter(
        (item) => !props.conversation.participants.find((participant) => participant.id === item.id),
    );
});
const newParticipants = ref([]);
const getContacts = async () => {
    try {
        state.loading.contacts = true;
        const {data, total} = await getContactsFromApi({
            active: true,
            filter: {
                search: searchQuery.value,
            },
        });
        contacts.value = data;
        totalContacts.value = total;
    } catch (errors) {
        console.log(errors);
    } finally {
        state.loading.contacts = false;
    }
};

const addParticipants = () => {
    if (!newParticipants.value.length) {
        toast.add({
            severity: "warn",
            summary: "No participants selected",
            detail: "Please select at least one participant to add.",
            life: 3000,
        });
        return;
    }
    emit("addParticipants", newParticipants.value);

    newParticipants.value = [];
};

// Participant menu
const selectedParticipant = computed(() =>
    props.conversation.participants.find((participant) => participant.id === state.show.participant),
);
const showParticipantMenu = computed({
    get: () => !!state.show.participant && !!state.show.participantMenu,
    set: (val) => (state.show.participantMenu = val),
});

const participantMenuItems = ref([
    {
        label: "",
        items: [] as Array<{
            label: string;
            icon: string;
            command: () => void;
        }>,
    },
]);
const toggleParticipantMenu = (participant) => {
    console.log("toggleParticipantMenu", participant);
    participantMenuItems.value[0].items = [
        {
            label: participant.id === $user.id ? "Leave" : "Remove",
            icon: "prime:trash",
            command: () => {
                emit("removeParticipant", participant);
                state.show.participantMenu = false;
            },
        },
    ];
    state.show.participant = participant.id;
    state.show.participantMenu = participant.id;
    participantMenuItems.value[0].label =
        selectedParticipant.value.first_name + " " + selectedParticipant.value.last_name;
};

watch(modelValue, () => {
    if (modelValue.value) {
        getContacts();
    }
});
watchDebounced(searchQuery, getContacts, {debounce: 1000});
</script>
<template>
    <Dialog v-model:visible="modelValue" header="Manage participants" class="w-full max-w-xl" modal>
        <div class="flex h-full grow flex-col">
            <div class="mb-8 flex flex-col">
                <Label for="new_participants" class="mb-1" required>Select participants</Label>
                <AutoComplete
                    id="new_participants"
                    dropdown
                    dropdown-mode="current"
                    multiple
                    :items="availableContacts"
                    v-model="newParticipants"
                    @complete="handleSearch"
                />
            </div>
            <div class="mb-10">
                <Button class="w-full bg-askr-green px-6 py-1.5 font-semibold text-white" @click="addParticipants">
                    <Icon icon="mdi:account-add-outline" width="18" />
                    <span class="ml-2">Add</span>
                </Button>
            </div>
            <h3 class="font-display mb-6 mt-auto text-xl font-semibold text-askr-green">Current participants</h3>
            <div v-auto-animate>
                <div
                    v-for="(participant, i) in conversation.participants"
                    :key="participant.id"
                    class="mb-4 flex w-full items-center"
                >
                    <Avatar
                        :user="participant"
                        class="flex h-8 w-8 shrink-0"
                        :class="[`bg-${participant.profile?.color?.class ?? 'stone-300'}`]"
                        shape="circle"
                    />
                    <div class="ml-3 mr-4">
                        <span v-if="participant.id === $user.id" class="font-medium">Me</span>
                        <div v-else>
                            <span class="font-medium">{{ participant.first_name }}</span>
                            <span class="ml-1.5 font-bold">{{ participant.last_name }}</span>
                        </div>
                    </div>
                    <div
                        class="ml-auto flex items-center"
                        v-if="participant.id === $user.id || conversation.user.id === $user.id"
                    >
                        <Button
                            type="button"
                            :icon="{icon: 'pepicons-pencil:dots-y', width: '24'}"
                            class="!pr-3 !ring-0"
                            @click.stop="() => toggleParticipantMenu(participant)"
                        />
                    </div>
                </div>
            </div>
        </div>
        <Dialog
            v-model:visible="showParticipantMenu"
            modal
            dismissable-mask
            class="overflow-hidden"
            :closable="false"
            :show-header="false"
        >
            <template #container>
                <div class="bg-white">
                    <Menu ref="menu" :model="participantMenuItems" />
                </div>
            </template>
        </Dialog>
    </Dialog>
</template>
